import * as t from 'io-ts';

import {makeApiType, RAccountIdRef} from './apiTypes';
import {parseCustomFieldAttribute, RCustomFieldAttribute} from './customFieldAttributeModel';

/*
 * Runtime type.
 */

const RAccountExternalInfo = t.type({
  id: t.string,
  source: t.string,
});

const RAccountDirty = t.readonly(
  t.intersection([
    t.type({
      id: t.Integer,
      name: t.string,
      alias: t.union([t.string, t.undefined]),
      avatarUrl: t.union([t.string, t.undefined]),
      description: t.union([t.string, t.undefined]),
      domains: t.readonlyArray(t.string),
      customFieldAttributes: t.union([t.readonlyArray(RCustomFieldAttribute), t.undefined]),
      createdAt: t.Integer,
      updatedAt: t.Integer,
    }),
    t.partial({
      shouldIgnoreThirdPartyLogo: t.boolean,
      externalInfo: RAccountExternalInfo,
    }),
  ]),
  'account',
);

const RAccountMetadata = t.readonly(
  t.type({
    numRecentConversations: t.Int,
    numCards: t.Int,
    conversationLimit: t.Int,
    cardLimit: t.Int,
  }),
  'accountMetadata',
);

/*
 * Type.
 */

export interface AccountModel extends t.TypeOf<typeof RAccountDirty> {}
export interface AccountMetadata extends t.TypeOf<typeof RAccountMetadata> {}
export interface AccountExternalInfoModel extends t.TypeOf<typeof RAccountExternalInfo> {}
export const RAccount = t.clean<AccountModel>(RAccountDirty as any);
export const RAccounts = t.readonlyArray(RAccount);
RAccountIdRef.setTargetType(RAccount);

/*
 * IO methods.
 */

export function parseAccount(src: any): AccountModel {
  return {
    id: src.id,
    name: src.name,
    alias: src.alias || undefined,
    avatarUrl: src.avatar_url || undefined,
    description: src.description || undefined,
    domains: src.domains || [],
    customFieldAttributes:
      (src.custom_field_attributes && src.custom_field_attributes.map(parseCustomFieldAttribute)) ||
      undefined,
    externalInfo: parseAccountExternalInfo(src.external_info),
    createdAt: src.updated_at || 0,
    updatedAt: src.created_at || 0,
  };
}

export function parseAccountExternalInfo(externalInfo?: any): AccountExternalInfoModel | undefined {
  if (!externalInfo) {
    return undefined;
  }
  return {
    id: externalInfo.id,
    source: externalInfo.source,
  };
}

function parsePreviewAccounts(src: any): ReadonlyArray<AccountModel> {
  return src.previews.map((accountSrc: any) => parseAccount(accountSrc));
}

function parseAccountMetadata(src: any): AccountMetadata {
  return {
    numRecentConversations: src.num_recent_conversations,
    numCards: src.num_cards,
    conversationLimit: src.conversation_limit,
    cardLimit: src.card_limit,
  };
}

/*
 * API type.
 */

export const ApiAccount = makeApiType('account', RAccount, parseAccount);
export const ApiPreviewAccounts = makeApiType('accounts', RAccounts, parsePreviewAccounts);
export const ApiAccountMetadata = makeApiType('accountMetadata', RAccountMetadata, parseAccountMetadata);
